/*
 * Fills in a YouTube video's runtime wherever the theme shows one — the
 * article meta row and the sidebar's related-video cards.
 *
 * A YouTube video's length is only knowable through YouTube itself, so the
 * badge ships empty from Liquid carrying data-youtube-id and is filled in
 * here. Three rules keep that off the critical path:
 *
 * Any element carrying data-youtube-id is filled in. It ships empty, which is
 * also the hook its stylesheet uses to draw a loading state, so nothing has to
 * be toggled by class. Three rules keep this off the critical path:
 *
 * - nothing runs until the page has finished loading, and then only when the
 *   browser is idle;
 * - a duration once known is kept in localStorage, so every later page view
 *   renders the badge immediately and requests nothing;
 * - the YouTube API is only loaded when there is at least one uncached id.
 *
 * Badges that cannot be resolved are removed, so a card never keeps a
 * permanent loading state.
 */
(() => {
  // The script tag can be emitted by more than one card on a listing page.
  if (window.jgVideoDurationLoaded) return;
  window.jgVideoDurationLoaded = true;

  const STORE_KEY = 'jg:yt-duration';
  const TTL = 90 * 24 * 60 * 60 * 1000; // a video's length does not change
  const API_SRC = 'https://www.youtube.com/iframe_api';
  const PER_VIDEO_TIMEOUT = 8000;

  const readCache = () => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeCache = (cache) => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(cache));
    } catch (error) {
      // Private mode or a full quota: the badges still work, they just refetch.
    }
  };

  const format = (seconds) => {
    const total = Math.round(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = String(total % 60).padStart(2, '0');
    return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
  };

  const show = (badge, seconds) => {
    badge.textContent = format(seconds);
  };

  let apiPromise = null;
  const loadApi = () => {
    if (apiPromise) return apiPromise;

    apiPromise = new Promise((resolve, reject) => {
      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }
      // The API calls one global hook; chain onto whatever already owns it.
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve();
      };
      const script = document.createElement('script');
      script.src = API_SRC;
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return apiPromise;
  };

  // One throwaway player per id, off-screen, destroyed as soon as it has
  // reported its duration.
  const fetchDuration = (id, host) =>
    new Promise((resolve, reject) => {
      const mount = document.createElement('div');
      host.appendChild(mount);

      let player = null;
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (player && player.destroy) player.destroy();
        mount.remove();
        fn(value);
      };

      const timer = setTimeout(() => finish(reject, new Error('timeout')), PER_VIDEO_TIMEOUT);

      player = new window.YT.Player(mount, {
        videoId: id,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 0, controls: 0 },
        events: {
          onReady: () => {
            const seconds = player.getDuration();
            if (seconds > 0) {
              finish(resolve, seconds);
            } else {
              finish(reject, new Error('no duration'));
            }
          },
          onError: () => finish(reject, new Error('player error')),
        },
      });
    });

  // Scoped to rootEl so content swapped in later (blog-filter.js) can be filled
  // without touching badges that already show a runtime.
  const run = async (rootEl = document) => {
    const badges = [...rootEl.querySelectorAll('[data-youtube-id]')].filter((badge) => !badge.textContent.trim());
    if (!badges.length) return;

    const cache = readCache();
    const now = Date.now();
    const pending = [];

    badges.forEach((badge) => {
      const entry = cache[badge.dataset.youtubeId];
      if (entry && now - entry.at < TTL) {
        show(badge, entry.seconds);
      } else {
        pending.push(badge);
      }
    });

    if (!pending.length) return;

    try {
      await loadApi();
    } catch (error) {
      pending.forEach((badge) => badge.remove());
      return;
    }

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;visibility:hidden';
    document.body.appendChild(host);

    // Sequential, so a card with three videos opens one player at a time.
    for (const badge of pending) {
      const id = badge.dataset.youtubeId;
      try {
        const seconds = await fetchDuration(id, host);
        cache[id] = { seconds, at: Date.now() };
        show(badge, seconds);
      } catch (error) {
        badge.remove();
      }
    }

    host.remove();
    writeCache(cache);
  };

  window.jgVideoDuration = { refresh: run };

  const start = () => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => run(), { timeout: 3000 });
    } else {
      setTimeout(run, 500);
    }
  };

  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
})();
